import React from 'react';
import classNames from 'classnames';
import { InlineEditor, IntersectionEditorInfo } from './InlineEditor';
import './EditableTextItem.less';
import { CellIconButton } from './CellIconButton';

export interface IEditableTextItemProps extends React.HTMLProps<HTMLDivElement> {
  isInDebug: boolean;
  text: string;
  showIgnoreButton?: boolean;
  /*
   * Determines whether to show editable value (inline editor)
   * @default true
   */
  showEditableValue?: boolean;
  /**
   * Determines whether to show actions (three dots) button
   * @default false
   */
  showActionsButton?: boolean;
  /**
   * Determines whether to show edit (pencil) button
   * @default true
   */
  showEditButton?: boolean;
  intersection: IntersectionEditorInfo;
  actionsProps?: {
    isActive?: boolean;
    isDisabled?: boolean;
    className?: string;
  };
  editProps?: {
    isActive?: boolean;
    isDisabled?: boolean;
    className?: string;
  };
  onEditClick: (event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => void;
  onActionsClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const getClassName = (className?: string, hasValue?: boolean) =>
  classNames('tt-editable-text-item', className, {
    "tt-editable-text-item--no-value": !hasValue
  });

/**
 * Displays given text (@param text), allows to edit it inline and has actions.
 * If you need just display readonly text, look at [[TextItem]]
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function EditableTextItem({
  text,
  isInDebug,
  className,
  intersection,
  onEditClick,
  onActionsClick,
  showEditableValue = true,
  showEditButton = true,
  showActionsButton,
  actionsProps,
  editProps,
  ...otherProps
}: IEditableTextItemProps) {
  return (
    <div className={getClassName(className)} {...otherProps}>
      {showEditableValue && (
        <InlineEditor onTextClick={onEditClick} intersection={intersection} text={text} isInDebug={isInDebug} />
      )}
      {!isInDebug && showEditButton && (
        <CellIconButton
          iconClassName={classNames(
            'tt-editable-text-item__edit-icon',
            {
              'tt-editable-text-item__ignore-icon--active': editProps?.isActive,
            },
            editProps?.className
          )}
          disabled={editProps?.isDisabled}
          onClick={onEditClick}
        />
      )}
      {!isInDebug && showActionsButton && (
        <CellIconButton
          iconClassName={classNames(
            'tt-editable-text-item__actions-icon',
            {
              'tt-editable-text-item__actions-icon--active': actionsProps?.isActive,
            },
            actionsProps?.className
          )}
          disabled={actionsProps?.isDisabled}
          onClick={onActionsClick}
        />
      )}
    </div>
  );
}
